import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { db } from '../../firebase-client';
import { collection, deleteDoc, doc, getDoc, getDocs } from 'firebase/firestore';
import useThreads from './hooks/useThreads';
import useThread from './hooks/useThread';
import useMessages from './hooks/useMessages';
import ThreadList from './ThreadList/ThreadList';
import ChatWindow from './ChatWindow/ChatWindow';
import BoardsView from './BoardsView';
import { extractSupportAdminIds } from '../../utils/supportAdmins';
import { safeOnSnapshot } from '../../utils/firestoreSubscriptions';
import { isMessageNotificationActionUrl, normalizeMessageNotification, summarizeMessageNotifications } from '../../utils/messageNotificationUtils';
import { setDirectThreadState, startDirectMessage } from '../../utils/messages';
import { useMyTeams } from '../teams/useMyTeams';
import { THREAD_FILTERS, getDeletedBefore, getThreadState, isThreadArchived } from './threadState';
import { getMessagesPageShellClassName } from './messageShellState';
import { usePwaInstallPrompt } from '../pwa/PwaInstallContext';
import { useCompactPwaViewport } from '../../hooks/useCompactPwaViewport';
import '../../styles/messages.css';

const INFO_BANNER_DISMISS_KEY = 'membernest:messages-info-banner-hidden-until';
const INFO_BANNER_DISMISS_MS = 7 * 24 * 60 * 60 * 1000;

function UnreadBadge({ count, label }) {
  if (!count) return null;
  return (
    <span className="message-unread-badge" aria-label={label || `${count} unread`}>
      {count > 99 ? '99+' : count}
    </span>
  );
}

function isInfoBannerDismissed() {
  try {
    const hiddenUntil = Number(window.localStorage.getItem(INFO_BANNER_DISMISS_KEY));
    if (!Number.isFinite(hiddenUntil)) return false;
    if (hiddenUntil > Date.now()) return true;
    window.localStorage.removeItem(INFO_BANNER_DISMISS_KEY);
  } catch (err) {}
  return false;
}

function isMessagingEnabled(team = {}) {
  return Boolean(team.modules?.communications?.messages?.enabled || team.modules?.messages?.enabled);
}

function getDisplayName(data = {}, fallback = 'Member') {
  const fullName = `${data.firstName || ''} ${data.lastName || ''}`.trim();
  return data.name || data.displayName || fullName || data.username || data.email || fallback;
}

async function addUserTarget(targetsById, uid, team = {}, roleLabel = 'Admin') {
  if (!uid || targetsById.has(uid)) return;
  const userSnap = await getDoc(doc(db, 'users', uid)).catch(() => null);
  const userData = userSnap?.exists() ? userSnap.data() || {} : {};
  targetsById.set(uid, {
    id: uid,
    name: getDisplayName(userData, roleLabel),
    roleLabel,
    teamName: team.name || 'Club',
  });
}

async function buildMemberTarget(memberDoc, team = {}, adminIds = []) {
  const memberData = memberDoc.data() || {};
  const userSnap = await getDoc(doc(db, 'users', memberDoc.id)).catch(() => null);
  const userData = userSnap?.exists() ? userSnap.data() || {} : {};
  const roleLabel = adminIds.includes(memberDoc.id) ? 'Admin' : 'Member';

  return {
    id: memberDoc.id,
    name: getDisplayName(userData, '') || getDisplayName(memberData, roleLabel),
    roleLabel,
    teamName: team.name || 'Club',
  };
}

function NewDirectMessageModal({ open, user, teams = [], onClose, onStarted }) {
  const availableTeams = React.useMemo(() => teams.filter(isMessagingEnabled), [teams]);
  const [teamId, setTeamId] = useState('');
  const [targets, setTargets] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [startingId, setStartingId] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setSearch('');
    setError('');
    setTeamId((current) => current && availableTeams.some((team) => team.id === current)
      ? current
      : availableTeams[0]?.id || '');
  }, [availableTeams, open]);

  useEffect(() => {
    if (!open || !teamId || !user?.uid) {
      setTargets([]);
      return undefined;
    }

    let cancelled = false;
    const team = availableTeams.find((entry) => entry.id === teamId);
    if (!team) {
      setTargets([]);
      return undefined;
    }

    const loadTargets = async () => {
      setLoading(true);
      setError('');
      const targetsById = new Map();
      const adminIds = Array.isArray(team.admins) ? team.admins.filter((uid) => uid && uid !== user.uid) : [];

      await Promise.all(adminIds.map((uid) => addUserTarget(targetsById, uid, team, 'Admin')));

      try {
        const membersSnap = await getDocs(collection(db, 'teams', team.id, 'members'));
        const memberTargets = await Promise.all(membersSnap.docs
          .filter((memberDoc) => memberDoc.id !== user.uid)
          .map((memberDoc) => buildMemberTarget(memberDoc, team, adminIds)));
        memberTargets.forEach((target) => targetsById.set(target.id, target));
      } catch (err) {
        // Regular members may only be allowed to message club admins.
      }

      if (!cancelled) {
        setTargets(Array.from(targetsById.values()).sort((a, b) => a.name.localeCompare(b.name)));
        setLoading(false);
      }
    };

    loadTargets().catch((err) => {
      if (!cancelled) {
        setTargets([]);
        setError(err?.message || 'Could not load message targets.');
        setLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [availableTeams, open, teamId, user?.uid]);

  if (!open) return null;

  const query = search.trim().toLowerCase();
  const visibleTargets = query
    ? targets.filter((target) => `${target.name} ${target.roleLabel}`.toLowerCase().includes(query))
    : targets;

  const handleStart = async (target) => {
    if (!teamId || !target?.id) return;
    setStartingId(target.id);
    setError('');
    try {
      const thread = await startDirectMessage(user, { id: target.id }, teamId);
      onStarted?.(thread.id);
      onClose?.();
    } catch (err) {
      setError(err?.message || 'Could not start conversation.');
    } finally {
      setStartingId('');
    }
  };

  return (
    <div className="message-compose-modal" role="presentation" onMouseDown={onClose}>
      <div className="message-compose-modal__panel" role="dialog" aria-modal="true" aria-label="Start a direct message" onMouseDown={(event) => event.stopPropagation()}>
        <div className="message-compose-modal__header">
          <h2>New message</h2>
          <button type="button" onClick={onClose} aria-label="Close new message">×</button>
        </div>

        {availableTeams.length > 0 ? (
          <>
            <label className="message-compose-modal__field">
              Club
              <select value={teamId} onChange={(event) => setTeamId(event.target.value)}>
                {availableTeams.map((team) => (
                  <option key={team.id} value={team.id}>{team.name || 'Unnamed club'}</option>
                ))}
              </select>
            </label>

            <label className="message-compose-modal__field">
              Search
              <input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Find a person" />
            </label>

            {error && <p className="text-danger message-compose-modal__error">{error}</p>}

            <div className="message-compose-modal__list">
              {loading ? (
                <p className="text-muted">Loading people...</p>
              ) : visibleTargets.length === 0 ? (
                <p className="text-muted">No available people for this club.</p>
              ) : (
                visibleTargets.map((target) => (
                  <button key={target.id} type="button" onClick={() => handleStart(target)} disabled={Boolean(startingId)}>
                    <span>
                      <strong>{target.name}</strong>
                      <small>{target.roleLabel} · {target.teamName}</small>
                    </span>
                    <span>{startingId === target.id ? 'Opening...' : 'Message'}</span>
                  </button>
                ))
              )}
            </div>
          </>
        ) : (
          <p className="text-muted message-compose-modal__empty">No clubs with direct messages enabled are available.</p>
        )}
      </div>
    </div>
  );
}

export default function MessagesPage() {
  const {
    user
  } = useAuth();
  const {
    threadId
  } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [threadFilter, setThreadFilter] = useState(THREAD_FILTERS.inbox);
  const [busyThreadId, setBusyThreadId] = useState(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [supportAdminIds, setSupportAdminIds] = useState([]);
  const [isNarrowMobile, setIsNarrowMobile] = useState(window.innerWidth < 768);
  const [showInfoBanner, setShowInfoBanner] = useState(() => !isInfoBannerDismissed());
  const [messageNotifications, setMessageNotifications] = useState([]);
  const { isStandalone } = usePwaInstallPrompt();
  const { isCompactPwaViewport } = useCompactPwaViewport(isStandalone);
  const isMobile = isNarrowMobile || isCompactPwaViewport;
  const requestedView = searchParams.get('view');
  const activeView = requestedView === 'direct' || threadId ? 'direct' : 'boards';
  const searchKey = searchParams.toString();
  const routeContext = React.useMemo(() => ({
    routeView: activeView,
    threadId: threadId || '',
    teamId: new URLSearchParams(searchKey).get('teamId') || '',
    boardId: new URLSearchParams(searchKey).get('boardId') || '',
    topicId: new URLSearchParams(searchKey).get('topicId') || '',
    panel: new URLSearchParams(searchKey).get('panel') || '',
  }), [activeView, searchKey, threadId]);
  const { teams } = useMyTeams();
  const teamNamesById = React.useMemo(() => teams.reduce((acc, team) => {
    if (team?.id) acc[team.id] = team.name || 'Unnamed club';
    return acc;
  }, {}), [teams]);
  const {
    threads,
    allThreads,
    threadsLoaded,
    loading: loadingThreads,
    userProfiles,
    threadStates
  } = useThreads(user, threadFilter, routeContext);
  const selectedThreadFromList = React.useMemo(
    () => (threadId ? (allThreads || []).find((entry) => entry.id === threadId) || null : null),
    [allThreads, threadId]
  );
  const canLoadSelectedThread = Boolean(threadId && selectedThreadFromList && threadsLoaded);
  const selectedThreadUnavailable = Boolean(threadId && !loadingThreads && !canLoadSelectedThread);
  const selectedThreadState = getThreadState(threadStates, threadId);
  const selectedThreadArchived = isThreadArchived(selectedThreadState);
  const {
    thread
  } = useThread(threadId, routeContext, user?.uid, canLoadSelectedThread);
  const selectedThread = canLoadSelectedThread ? thread || selectedThreadFromList : null;
  const {
    messages
  } = useMessages(
    canLoadSelectedThread ? threadId : '',
    user?.uid,
    getDeletedBefore(selectedThreadState),
    routeContext,
    selectedThread?.participants || []
  );
  const unreadSummary = React.useMemo(
    () => summarizeMessageNotifications(messageNotifications),
    [messageNotifications]
  );

  // Detect mobile/desktop
  useEffect(() => {
    const handleResize = () => setIsNarrowMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  useEffect(() => {
    const fetchSupportAdmins = async () => {
      try {
        const snap = await getDoc(doc(db, 'appSettings', 'support'));
        if (snap.exists()) {
          const data = snap.data();
          const adminIds = extractSupportAdminIds(data);
          setSupportAdminIds(adminIds);
        }
      } catch (err) {}
    };
    fetchSupportAdmins();
  }, []);
  useEffect(() => {
    if (!user?.uid) {
      setMessageNotifications([]);
      return undefined;
    }

    const notificationsRef = collection(db, 'notifications', user.uid, 'items');
    const unsubscribe = safeOnSnapshot(notificationsRef, (snap) => {
      const activeThreadDeletes = [];
      const visibleNotifications = [];

      snap.docs.forEach((noteDoc) => {
        const note = { id: noteDoc.id, ...(noteDoc.data() || {}) };
        const actionUrl = typeof note.actionUrl === 'string' ? note.actionUrl.trim() : '';
        if (!isMessageNotificationActionUrl(actionUrl)) return;

        const normalized = normalizeMessageNotification(note);
        const noteThreadId = normalized?.type === 'direct' ? normalized.threadId : note.threadId || noteDoc.id;
        if (threadId && noteThreadId === threadId) {
          activeThreadDeletes.push(deleteDoc(noteDoc.ref));
          return;
        }

        visibleNotifications.push(note);
      });

      if (activeThreadDeletes.length > 0) {
        Promise.all(activeThreadDeletes).catch(() => {});
      }
      setMessageNotifications(visibleNotifications);
    }, () => {
      setMessageNotifications([]);
    }, { label: 'messages.notifications', uid: user.uid, ...routeContext });

    return () => unsubscribe();
  }, [routeContext, user?.uid, threadId]);
  if (!user) {
    return null;
  }
  const handleBackToThreads = () => {
    const query = searchParams.toString();
    navigate(`/messages${query ? `?${query}` : ''}`);
  };
  const showThreadList = !isMobile || !threadId || selectedThreadUnavailable;
  const showChatWindow = !isMobile || (!!threadId && !selectedThreadUnavailable);
  const handleTabChange = view => {
    const next = new URLSearchParams(searchParams);
    if (view === 'direct') {
      next.set('view', 'direct');
      setSearchParams(next);
      return;
    }

    next.delete('view');
    navigate(`/messages${next.toString() ? `?${next.toString()}` : ''}`);
  };
  const handleDismissInfoBanner = () => {
    setShowInfoBanner(false);
    try {
      window.localStorage.setItem(INFO_BANNER_DISMISS_KEY, String(Date.now() + INFO_BANNER_DISMISS_MS));
    } catch (err) {}
  };
  const handleThreadStateAction = async (id, action) => {
    if (!id) return;
    setBusyThreadId(id);
    try {
      await setDirectThreadState(id, action);
      if (threadId === id) {
        handleBackToThreads();
      }
    } catch (err) {
      window.alert(err?.message || 'Could not update this conversation.');
    } finally {
      setBusyThreadId(null);
    }
  };
  const handleArchiveThread = id => handleThreadStateAction(id, 'archive');
  const handleRestoreThread = id => handleThreadStateAction(id, 'restoreArchive');
  const handleDeleteThread = id => {
    const confirmed = window.confirm(
      'Delete this conversation for you? Messages will be deleted from this account only. The other person may still see them, and they may reopen this chat by responding.'
    );
    if (!confirmed) return;
    handleThreadStateAction(id, 'deleteForMe');
  };
  const renderInfoBanner = () => {
    if (!showInfoBanner) return null;
    return <div className="chat-info-banner">
        <button type="button" className="chat-info-banner__dismiss" onClick={handleDismissInfoBanner} aria-label="Hide this notice for 7 days" title="Hide for 7 days">
          ✕
        </button>
        <p>
          <strong>Message safety:</strong>
          Support chats show a <span className="support-badge">Support</span> label.
          Member Nest will never ask for card, bank, or password details in chat.
        </p>
      </div>;
  };
  const pageClassName = getMessagesPageShellClassName({
    activeView,
    threadId,
    boardTopicId: routeContext.topicId,
    isMobile,
    isCompactPwaViewport,
  });

  return <div className={pageClassName}>
      <div className="messages-page-header">
        <div className="messages-page-header__copy">
          <h1 className="search-title">Messages</h1>
          <p className="messages-page-subtitle">
            Move between one-to-one conversations and club-wide discussion boards without losing context.
          </p>
        </div>

        <div className="messages-top-tabs" role="tablist" aria-label="Message views">
          <button type="button" className={`messages-top-tab ${activeView === 'direct' ? 'active' : ''}`} onClick={() => handleTabChange('direct')}>
            <span>Direct Messages</span>
            <UnreadBadge count={unreadSummary.direct.total} label={`${unreadSummary.direct.total} unread direct messages`} />
          </button>
          <button type="button" className={`messages-top-tab ${activeView === 'boards' ? 'active' : ''}`} onClick={() => handleTabChange('boards')}>
            <span>Boards</span>
            <UnreadBadge count={unreadSummary.boards.total} label={`${unreadSummary.boards.total} unread board updates`} />
          </button>
        </div>
      </div>

      {/* Info box */}
      {activeView === 'direct' ? <>
          {showThreadList && renderInfoBanner()}

          <div className="messages-layout">
            {showThreadList && <ThreadList key="direct-thread-list" threads={threads} loading={loadingThreads} activeThreadId={threadId} onSelect={id => navigate(`/messages/${id}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`)} userProfiles={userProfiles} currentUserId={user?.uid} supportAdminIds={supportAdminIds} unreadCountsByThread={unreadSummary.direct.byThread} filter={threadFilter} onFilterChange={setThreadFilter} onNewThread={() => setComposeOpen(true)} teamNamesById={teamNamesById} />}

            {showChatWindow && <ChatWindow key={`direct-chat-${threadId || 'none'}`} thread={selectedThread} messages={messages} user={user} userProfiles={userProfiles} supportAdminIds={supportAdminIds} isMobile={isMobile} onBack={handleBackToThreads} isArchived={selectedThreadArchived} onArchive={handleArchiveThread} onRestore={handleRestoreThread} onDelete={handleDeleteThread} actionBusy={busyThreadId === threadId} teamNamesById={teamNamesById} routeContext={routeContext} />}
          </div>
          <NewDirectMessageModal open={composeOpen} user={user} teams={teams} onClose={() => setComposeOpen(false)} onStarted={(id) => navigate(`/messages/${id}?view=direct`)} />
        </> : <BoardsView user={user} searchParams={searchParams} setSearchParams={setSearchParams} unreadSummary={unreadSummary.boards} messageNotifications={messageNotifications} />}
    </div>;
}
